import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Flex } from "theme-ui";
import { Button, Heading } from "@loanpal/lumos__core";
import numeral from "numeral";
import {
  createPaymentCustomer,
  createPaymentInvoice,
  createPaymentTransaction,
  getStates,
  getUsers,
  resolveInvoiceLink,
} from "../../utils/api";
import { numberToMoney } from "../../utils/format";
import StreetAddressAutocomplete from "./StreetAddressAutocomplete";

const STEPS = ["Customer information", "Order summary", "Create Invoice"];

const fieldSx = {
  width: "100%",
  height: 40,
  px: 2,
  border: "1px solid",
  borderColor: "neutral.l2",
  borderRadius: 1,
  fontSize: 1,
  boxSizing: "border-box",
  bg: "white",
};

const labelSx = { display: "block", mb: 1, fontSize: 1 };

function Field({ id, label, description, children }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Box as="label" htmlFor={id} sx={labelSx}>
        {label}
      </Box>
      {description ? (
        <Box sx={{ mb: 1, fontSize: 0, color: "neutral.d1" }}>{description}</Box>
      ) : null}
      {children}
    </Box>
  );
}

const emptyCustomerForm = () => ({
  email: "",
  firstName: "",
  lastName: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
  referenceId: "",
  amount: "",
  description: "",
});

function userLabel(user) {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  if (user.email && name) return `${user.email} — ${name}`;
  return user.email ?? name ?? user.id;
}

function actorFromUser(user) {
  if (!user?.email) return undefined;
  return {
    email: user.email,
    firstName: user.firstName?.trim() || "—",
    lastName: user.lastName?.trim() || "—",
  };
}

function parseAmount(value) {
  const n = numeral(value).value();
  if (n == null || !Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 100) / 100;
}

function dollarsToCents(dollars) {
  return Math.round(dollars * 100);
}

function formatAddress(form) {
  const street = [form.line1.trim(), form.line2.trim()].filter(Boolean).join(" ");
  const cityStateZip = [
    form.city.trim(),
    [form.state.trim(), form.postalCode.trim()].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return [street, cityStateZip].filter(Boolean).join(" ");
}

function normalizeUsers(response) {
  const list = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];
  return list.filter((u) => u?.email && u?.id);
}

function normalizeStates(response) {
  const list = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];
  return list
    .map((s) => ({
      code: String(s?.code ?? ""),
      name: String(s?.name ?? s?.code ?? ""),
    }))
    .filter((s) => s.code);
}

/**
 * Multi-step payments invoice flow (ported from payments-temp).
 * Gate (paymentsEnabled) is handled by the parent pay page.
 */
export default function PaymentsFlow({
  clientId,
  elevatedToken,
  paymentsConfig,
  partnerName,
}) {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState(emptyCustomerForm);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [states, setStates] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [transactionResult, setTransactionResult] = useState(null);
  const [invoiceResult, setInvoiceResult] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId) ?? null,
    [users, selectedUserId]
  );
  const actor = useMemo(() => actorFromUser(selectedUser), [selectedUser]);

  const hasAddressInput = Boolean(
    form.line1.trim() ||
      form.city.trim() ||
      form.state.trim() ||
      form.postalCode.trim()
  );

  const canContinueStep0 =
    Boolean(selectedUserId && actor) &&
    Boolean(form.email.trim() && form.firstName.trim() && form.lastName.trim()) &&
    (!hasAddressInput ||
      Boolean(
        form.line1.trim() &&
          form.city.trim() &&
          form.state.trim() &&
          form.postalCode.trim()
      ));

  const canContinueStep1 =
    Boolean(form.description.trim()) && parseAmount(form.amount) !== null;

  const apiEnvironment =
    paymentsConfig?.apiEnvironment === "production" ? "production" : "sandbox";

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    setUsersLoading(true);
    Promise.all([getUsers(clientId, elevatedToken), getStates(clientId)])
      .then(([usersResult, statesData]) => {
        if (cancelled) return;
        setUsers(normalizeUsers(usersResult));
        setStates(normalizeStates(statesData));
      })
      .finally(() => {
        if (!cancelled) setUsersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, elevatedToken]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateAmount = (raw) => {
    if (/[$,]/.test(raw) || /\.\d{2}$/.test(raw)) {
      const n = numeral(raw).value();
      if (n != null && Number.isFinite(n) && n >= 0) {
        setForm((prev) => ({
          ...prev,
          amount: n === 0 ? "" : String(Math.round(n)),
        }));
        return;
      }
    }
    const digits = raw.replace(/[^0-9]/g, "");
    setForm((prev) => ({ ...prev, amount: digits }));
  };

  const handleStreetPlaceSelected = useCallback((address) => {
    setForm((prev) => ({
      ...prev,
      line1: address.line1,
      line2: address.line2 || prev.line2,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
    }));
  }, []);

  const clearForm = () => {
    setForm(emptyCustomerForm());
    setSelectedUserId("");
    setTransactionResult(null);
    setInvoiceResult(null);
    setSubmitError("");
    setLinkCopied(false);
    setActiveStep(0);
  };

  const handleContinue = () => {
    if (activeStep === 0 && canContinueStep0) setActiveStep(1);
    else if (activeStep === 1 && canContinueStep1) setActiveStep(2);
  };

  const handleBack = () => {
    setSubmitError("");
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const buildCustomerPayload = useCallback(() => {
    const payload = {
      email: form.email.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      actor,
    };
    if (hasAddressInput) {
      payload.address = {
        line1: form.line1.trim(),
        line2: form.line2.trim() || undefined,
        city: form.city.trim(),
        state: form.state.trim(),
        postalCode: form.postalCode.trim(),
      };
    }
    return payload;
  }, [actor, form, hasAddressInput]);

  const handleGenerateInvoiceLink = async () => {
    if (!actor || !clientId) return;
    const amount = parseAmount(form.amount);
    if (amount === null) return;

    setSubmitting(true);
    setSubmitError("");
    setTransactionResult(null);
    setInvoiceResult(null);
    setLinkCopied(false);

    try {
      const customer = await createPaymentCustomer(
        clientId,
        buildCustomerPayload(),
        elevatedToken
      );
      if (!customer?.id) throw new Error("Failed to create customer.");

      const transaction = await createPaymentTransaction(
        clientId,
        {
          amount: dollarsToCents(amount),
          customerId: customer.id,
          description: form.description.trim(),
          referenceNumber: form.referenceId.trim() || undefined,
          actor,
        },
        elevatedToken
      );
      if (!transaction?.id) throw new Error("Failed to create transaction.");
      setTransactionResult(transaction);

      const invoice = await createPaymentInvoice(
        clientId,
        { transactionId: transaction.id, actor },
        elevatedToken
      );
      if (!invoice?.id) throw new Error("Failed to create invoice.");

      const link = resolveInvoiceLink(invoice, apiEnvironment);
      setInvoiceResult({ ...invoice, url: link });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to generate invoice link."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const invoiceLink = invoiceResult
    ? resolveInvoiceLink(invoiceResult, apiEnvironment)
    : null;

  const amountParsed = parseAmount(form.amount);
  const showNextSteps = activeStep >= 1 && !invoiceResult;

  return (
    <Box>
      <Heading variant="h5" sx={{ mb: 3 }}>
        Payments for {partnerName}
      </Heading>

      <Flex
        sx={{
          gap: 2,
          mb: 4,
          flexWrap: "wrap",
          fontSize: 1,
          color: "neutral.d1",
        }}
      >
        {STEPS.map((label, i) => (
          <Box
            key={label}
            sx={{
              fontWeight: i === activeStep ? 700 : 400,
              color: i === activeStep ? "primary.base" : "inherit",
            }}
          >
            {i + 1}. {label}
            {i < STEPS.length - 1 ? " →" : ""}
          </Box>
        ))}
      </Flex>

      <Flex
        sx={{
          gap: 4,
          flexDirection: ["column", "column", showNextSteps ? "row" : "column"],
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          {activeStep === 0 && (
            <Box>
              <Field
                id="pay-user"
                label="User"
                description="Select the sales rep (used as payment actor)"
              >
                <Box
                  as="select"
                  id="pay-user"
                  value={selectedUserId}
                  disabled={usersLoading}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  sx={fieldSx}
                >
                  <option value="">
                    {usersLoading ? "Loading users…" : "Select user"}
                  </option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {userLabel(u)}
                    </option>
                  ))}
                </Box>
              </Field>

              <Flex
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Heading variant="h6" as="h3">
                  Customer details
                </Heading>
                <Button variant="ghost" type="button" onClick={clearForm}>
                  Clear form
                </Button>
              </Flex>

              <Field id="pay-email" label="Email">
                <Box
                  as="input"
                  id="pay-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  sx={fieldSx}
                />
              </Field>
              <Flex sx={{ gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "1 1 200px" }}>
                  <Field id="pay-first" label="First name">
                    <Box
                      as="input"
                      id="pay-first"
                      value={form.firstName}
                      onChange={(e) => updateField("firstName", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
                <Box sx={{ flex: "1 1 200px" }}>
                  <Field id="pay-last" label="Last name">
                    <Box
                      as="input"
                      id="pay-last"
                      value={form.lastName}
                      onChange={(e) => updateField("lastName", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
              </Flex>

              <Heading variant="h6" as="h3" sx={{ mb: 2 }}>
                Project address{" "}
                <Box as="span" sx={{ fontWeight: 400, color: "neutral.d1" }}>
                  (optional)
                </Box>
              </Heading>

              <Box sx={{ mb: 3 }}>
                <StreetAddressAutocomplete
                  label="Street address"
                  value={form.line1}
                  onChange={(v) => updateField("line1", v)}
                  onPlaceSelected={handleStreetPlaceSelected}
                />
              </Box>
              <Field id="pay-line2" label="Address 2 (optional)">
                <Box
                  as="input"
                  id="pay-line2"
                  value={form.line2}
                  onChange={(e) => updateField("line2", e.target.value)}
                  sx={fieldSx}
                />
              </Field>
              <Flex sx={{ gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: "2 1 160px" }}>
                  <Field id="pay-city" label="City">
                    <Box
                      as="input"
                      id="pay-city"
                      value={form.city}
                      onChange={(e) => updateField("city", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
                <Box sx={{ flex: "1 1 100px" }}>
                  <Field id="pay-state" label="State">
                    <Box
                      as="select"
                      id="pay-state"
                      value={form.state}
                      onChange={(e) => updateField("state", e.target.value)}
                      sx={fieldSx}
                    >
                      <option value="">Select</option>
                      {states.map((s) => (
                        <option key={s.code} value={s.code}>
                          {s.code}
                        </option>
                      ))}
                    </Box>
                  </Field>
                </Box>
                <Box sx={{ flex: "1 1 100px" }}>
                  <Field id="pay-zip" label="ZIP">
                    <Box
                      as="input"
                      id="pay-zip"
                      value={form.postalCode}
                      onChange={(e) => updateField("postalCode", e.target.value)}
                      sx={fieldSx}
                    />
                  </Field>
                </Box>
              </Flex>

              <Box
                sx={{
                  borderTop: "1px solid",
                  borderColor: "neutral.l3",
                  pt: 3,
                }}
              >
                <Field id="pay-ref" label="Reference ID (optional)">
                  <Box
                    as="input"
                    id="pay-ref"
                    value={form.referenceId}
                    onChange={(e) => updateField("referenceId", e.target.value)}
                    sx={fieldSx}
                  />
                </Field>
              </Box>

              <Button
                type="button"
                onClick={handleContinue}
                disabled={!canContinueStep0}
              >
                Continue
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Button
                variant="ghost"
                type="button"
                onClick={handleBack}
                sx={{ mb: 3 }}
              >
                ← Back
              </Button>
              <Heading variant="h6" as="h3" sx={{ mb: 3 }}>
                Order summary
              </Heading>
              <Field id="pay-desc" label="Description">
                <Box
                  as="input"
                  id="pay-desc"
                  value={form.description}
                  onChange={(e) => updateField("description", e.target.value)}
                  sx={fieldSx}
                />
              </Field>
              <Field id="pay-amount" label="Amount (USD)">
                <Box
                  as="input"
                  id="pay-amount"
                  value={form.amount}
                  onChange={(e) => updateAmount(e.target.value)}
                  onBlur={() => {
                    const n = parseAmount(form.amount);
                    if (n != null) {
                      setForm((prev) => ({ ...prev, amount: String(n) }));
                    }
                  }}
                  sx={fieldSx}
                />
              </Field>
              <Button
                type="button"
                onClick={handleContinue}
                disabled={!canContinueStep1}
              >
                Continue
              </Button>
            </Box>
          )}

          {activeStep === 2 && !invoiceResult && (
            <Box>
              <Button
                variant="ghost"
                type="button"
                onClick={handleBack}
                disabled={submitting}
                sx={{ mb: 3 }}
              >
                ← Back
              </Button>
              <Heading variant="h6" as="h3" sx={{ mb: 2 }}>
                Create Invoice
              </Heading>
              <Box sx={{ mb: 2, color: "neutral.d1", fontSize: 1 }}>
                Review the next steps panel, then generate an invoice link for
                the customer.
              </Box>
              {submitError ? (
                <Box sx={{ mb: 2, color: "danger.base" }}>{submitError}</Box>
              ) : null}
              <Button
                type="button"
                onClick={handleGenerateInvoiceLink}
                disabled={submitting}
              >
                {submitting ? "Generating…" : "Generate invoice link"}
              </Button>
            </Box>
          )}

          {invoiceResult && invoiceLink && (
            <Box>
              <Heading variant="h6" as="h3" sx={{ mb: 2 }}>
                Invoice created
              </Heading>
              <Box sx={{ mb: 2, wordBreak: "break-all" }}>
                <a href={invoiceLink} target="_blank" rel="noreferrer">
                  {invoiceLink}
                </a>
              </Box>
              <Flex sx={{ gap: 2, flexWrap: "wrap" }}>
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(invoiceLink);
                      setLinkCopied(true);
                    } catch {
                      setLinkCopied(false);
                    }
                  }}
                >
                  {linkCopied ? "Copied" : "Copy link"}
                </Button>
                <Button type="button" variant="secondary" onClick={clearForm}>
                  Create another
                </Button>
              </Flex>
              {(form.referenceId.trim() ||
                transactionResult?.referenceNumber ||
                invoiceResult?.referenceNumber) && (
                <Box sx={{ mt: 3, fontSize: 1, color: "neutral.d1" }}>
                  Reference:{" "}
                  {form.referenceId.trim() ||
                    transactionResult?.referenceNumber ||
                    invoiceResult?.referenceNumber}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {showNextSteps && (
          <Box
            sx={{
              width: ["100%", "100%", 320],
              flexShrink: 0,
              border: "1px solid",
              borderColor: "neutral.l3",
              borderRadius: 2,
              p: 3,
              position: ["static", "static", "sticky"],
              top: 24,
            }}
          >
            <Heading variant="h6" as="h3" sx={{ mb: 3 }}>
              Next steps
            </Heading>
            <Box sx={{ mb: 3 }}>
              <Flex
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Box sx={{ fontWeight: 600, fontSize: 1 }}>
                  Customer information
                </Box>
                <Button
                  variant="ghost"
                  type="button"
                  onClick={() => setActiveStep(0)}
                >
                  Edit
                </Button>
              </Flex>
              <Box sx={{ fontSize: 1, color: "neutral.d1" }}>
                <div>{`${form.firstName} ${form.lastName}`.trim() || "—"}</div>
                <div>{form.email.trim() || "—"}</div>
                {hasAddressInput ? <div>{formatAddress(form)}</div> : null}
                {form.referenceId.trim() ? (
                  <div>{form.referenceId.trim()}</div>
                ) : null}
              </Box>
            </Box>
            <Box
              sx={{
                borderTop: "1px solid",
                borderColor: "neutral.l3",
                pt: 3,
              }}
            >
              <Flex
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Box sx={{ fontWeight: 600, fontSize: 1 }}>Order summary</Box>
                {activeStep > 1 &&
                amountParsed != null &&
                form.description.trim() ? (
                  <Button
                    variant="ghost"
                    type="button"
                    onClick={() => setActiveStep(1)}
                  >
                    Edit
                  </Button>
                ) : null}
              </Flex>
              {amountParsed != null && form.description.trim() ? (
                <Box sx={{ fontSize: 1 }}>
                  <Box sx={{ mb: 1 }}>{form.description.trim()}</Box>
                  <Flex
                    sx={{ justifyContent: "space-between", fontWeight: 700 }}
                  >
                    <span>Total</span>
                    <span>{numberToMoney(String(amountParsed))}</span>
                  </Flex>
                </Box>
              ) : (
                <Box sx={{ fontSize: 1, color: "neutral.d1" }}>
                  Enter amount and description
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Flex>
    </Box>
  );
}
